import { EditSchedule } from "@/containers/Project/components";
import * as S from "@/containers/Project/components/styled";
import { useMobileLayout } from "@/hooks/useAppMedia";
import { useProjectContext } from "@/providers";
import { useUpdateProjectSchedulesMutation } from "@/services";
import { ResponsiveModal } from "@/sharedStyled";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Row } from "antd";
import { bool, func } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

const DEFAULT_SCHEDULES = ["A", "B", "C", "D", "E"];
const DefaultScheduleCount = DEFAULT_SCHEDULES.length;

export const ManageProjectScheduleModal = ({ isOpen, onCancel }) => {
  const intl = useIntl();
  const isMobileLayout = useMobileLayout();

  const { manageSchedulesModalOpen, project, setProject } = useProjectContext();

  const [schedules, setSchedules] = useState([]);
  const [toRemove, setToRemove] = useState([]);
  const [originalNames, setOriginalNames] = useState({});
  const [showDupeNamesError, setShowDupeNamesError] = useState(false);

  const [updateProjectSchedules, { isLoading }] =
    useUpdateProjectSchedulesMutation();

  useEffect(() => {
    if (project?.schedules?.length > 0 && manageSchedulesModalOpen) {
      setToRemove([]);
      setOriginalNames(
        Object.fromEntries(
          project.schedules.map((schedule) => [schedule.id, schedule.name])
        )
      );
      setSchedules(
        project.schedules
          .map((schedule) =>
            !schedule.isDeleted
              ? { id: schedule.id, name: schedule.name }
              : null
          )
          .filter((schedule) => schedule)
      );
    }
  }, [project, manageSchedulesModalOpen]);

  useEffect(() => {
    if (schedules?.length > 0) {
      // check for duplicate schedule names
      const names = schedules.map((schedule) => schedule.name);
      const dupeNames = names.some(
        (name, index) => names.indexOf(name) !== index
      );
      setShowDupeNamesError(dupeNames);
    }
  }, [schedules]);

  const handleAdd = () => {
    const newSchedule = {
      name: `Schedule ${DEFAULT_SCHEDULES[schedules.length]}`,
    };
    setSchedules([...schedules, newSchedule]);
  };

  const handleDelete = (index) => {
    if (schedules[index]?.id) {
      setToRemove([...toRemove, schedules[index].id]);
    }

    const updatedSchedules = schedules.filter(
      (schedule) => schedule !== schedules[index]
    );

    setSchedules(updatedSchedules);
  };

  const handleRename = (schedule, newValue) => {
    const updatedSchedules = schedules.map((scheduleItem) => {
      if (scheduleItem === schedule) {
        scheduleItem.name = newValue;
      }
      return scheduleItem;
    });

    setSchedules(updatedSchedules);
  };

  const handleChangeStartingMilestone = (schedule, newValue) => {
    const updatedSchedules = schedules.map((scheduleItem) => {
      if (scheduleItem === schedule) {
        scheduleItem.tempStartingMilestone = newValue;
      }
      return scheduleItem;
    });
    setSchedules(updatedSchedules);
  };

  const handleSave = () => {
    const schedulesToCreate = schedules
      .filter((scheduleItem) => !scheduleItem.id)
      .map((scheduleItem) => ({
        name: scheduleItem.name,
        startingMilestoneId: scheduleItem.tempStartingMilestone ?? null,
      }));

    const schedulesToUpdate = schedules
      .filter(
        (scheduleItem) =>
          scheduleItem.id &&
          scheduleItem.name !== originalNames[scheduleItem.id]
      )
      .map((scheduleItem) => ({
        id: scheduleItem.id,
        name: scheduleItem.name,
      }));

    if (
      !schedulesToCreate.length &&
      !schedulesToUpdate.length &&
      !toRemove.length
    ) {
      onCancel();
      return;
    }

    updateProjectSchedules({
      projectId: project.id,
      schedulesToCreate,
      schedulesToUpdate,
      scheduleIdsToDelete: toRemove,
    })
      .unwrap()
      .then(({ project, errors }) => {
        setProject(project);
        if (errors?.length > 0) {
          const messageIds = {
            add: "addScheduleModal.errorAdd",
            delete: "addScheduleModal.errorDelete",
            rename: "addScheduleModal.errorRename",
          };
          Object.entries(messageIds).forEach(([operation, id]) => {
            const names = errors
              .filter((error) => error.operation === operation)
              .map((error) => error.scheduleName);
            if (names.length > 0) {
              toast.error(
                intl.formatMessage({ id }, { names: names.join(", ") })
              );
            }
          });
          return;
        }
        toast.success(
          intl.formatMessage({
            id: "addScheduleModal.saved",
          })
        );
        onCancel();
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({
            id: "addScheduleModal.error",
          })
        );
      });
  };

  return (
    <ResponsiveModal
      $mobile={isMobileLayout}
      maskClosable={false}
      title={intl.formatMessage({
        id: "addScheduleModal.title",
      })}
      onOk={handleSave}
      onCancel={onCancel}
      okButtonProps={{
        loading: isLoading,
        disabled:
          isLoading ||
          !schedules.length ||
          (schedules?.length > 0 &&
            schedules.filter(
              (schedule) => !schedule.name || !schedule.name.trim().length
            ).length > 0) ||
          showDupeNamesError,
      }}
      open={isOpen}
      okText={intl.formatMessage({
        id: "general.save",
      })}
    >
      <Row>
        <span>
          {intl.formatMessage({
            id: "addScheduleModal.description",
          })}
        </span>
      </Row>
      <div>
        {schedules.length > 0 &&
          schedules.map((schedule, index) => (
            <EditSchedule
              key={`edit-schedule-${index}`}
              schedule={schedule}
              index={index}
              label={`Schedule ${DEFAULT_SCHEDULES[index]}`}
              handleDelete={handleDelete}
              handleRename={handleRename}
              handleChangeStartingMilestone={handleChangeStartingMilestone}
            />
          ))}
      </div>
      {!(schedules.length >= DefaultScheduleCount) && (
        <Button
          data-test-id={`add-schedule-btn`}
          onClick={handleAdd}
          icon={<FontAwesomeIcon icon={faCirclePlus} />}
        >
          {intl.formatMessage({
            id: "addScheduleModal.addNew",
          })}
        </Button>
      )}
      {showDupeNamesError && (
        <Row>
          <S.ErrorText>
            {intl.formatMessage({
              id: "addScheduleModal.dupeMessage",
            })}
          </S.ErrorText>
        </Row>
      )}
    </ResponsiveModal>
  );
};

ManageProjectScheduleModal.propTypes = {
  isOpen: bool,
  onCancel: func.isRequired,
};
